import { useState } from 'react';

export default function NewsletterBanner() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);

    const formData = new URLSearchParams();
    formData.append('email', email.trim());

    try {
      await fetch('https://readdy.ai/api/form/d6obfn6ancq2eie81ro0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden border border-orange-100">
          {/* Decorative background blobs */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-orange-100 rounded-full -translate-y-1/2 translate-x-1/3 opacity-60 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-amber-100 rounded-full translate-y-1/2 -translate-x-1/4 opacity-60 pointer-events-none"></div>

          <div className="relative z-10 grid lg:grid-cols-2 gap-0">
            {/* Left: Image + perks */}
            <div className="relative hidden lg:block">
              <img
                src="https://public.readdy.ai/ai/img_res/1eb9cfbbb9d96b3d43e51b847583541f.jpg"
                alt="Students enjoying free math tips"
                className="w-full h-full object-cover object-top"
                style={{ minHeight: '380px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
              <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-2">
                {['Free Math Tips', 'Weekly Updates', 'Exclusive Worksheets', 'Test Prep Alerts'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-white/90 text-orange-700 text-xs font-bold rounded-full shadow whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              {!submitted ? (
                <>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-xs font-bold mb-5 w-fit whitespace-nowrap">
                    <i className="ri-mail-send-line text-base"></i>
                    FREE NEWSLETTER — JOIN 5,000+ LEARNERS
                  </div>

                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                    Get Free Math Tips &amp; <span className="text-orange-600">Weekly Updates</span>
                  </h2>
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    Subscribe and receive Mr. Augustine's best math strategies, free worksheets, test prep alerts, and exclusive learning resources — delivered straight to your inbox every week.
                  </p>

                  {/* Perks list */}
                  <ul className="space-y-2 mb-7">
                    {[
                      { icon: 'ri-lightbulb-flash-line', text: 'Weekly math tips & shortcuts from Mr. Augustine' },
                      { icon: 'ri-file-download-line', text: 'Free downloadable worksheets for every grade level' },
                      { icon: 'ri-notification-3-line', text: 'Early access to new lessons & test prep materials' },
                      { icon: 'ri-gift-2-line', text: 'Exclusive subscriber-only discounts on memberships' },
                    ].map((perk) => (
                      <li key={perk.text} className="flex items-start gap-3 text-sm text-gray-700">
                        <div className="w-6 h-6 flex items-center justify-center text-orange-600 flex-shrink-0 mt-0.5">
                          <i className={`${perk.icon} text-base`}></i>
                        </div>
                        <span>{perk.text}</span>
                      </li>
                    ))}
                  </ul>

                  <form
                    data-readdy-form
                    id="newsletter-subscription-form"
                    onSubmit={handleSubmit}
                    className="space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all"
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-7 py-3 bg-orange-600 text-white rounded-xl font-semibold text-sm hover:bg-orange-700 transition-all hover:scale-105 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Subscribing…
                          </>
                        ) : (
                          <>
                            Subscribe Free
                            <i className="ri-arrow-right-line"></i>
                          </>
                        )}
                      </button>
                    </div>
                    {error && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <i className="ri-error-warning-line"></i> {error}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-shield-check-line text-green-500"></i>
                      No spam, ever. Unsubscribe anytime. Your privacy is protected.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                    <i className="ri-checkbox-circle-fill text-4xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">You're In! 🎉</h3>
                  <p className="text-gray-600 text-sm mb-6 max-w-xs mx-auto">
                    Welcome to the Math Mastery community! Check your inbox for your first free math tips from Mr. Augustine.
                  </p>
                  <a
                    href="#pricing"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-orange-600 text-white rounded-xl font-semibold text-sm hover:bg-orange-700 transition-all whitespace-nowrap"
                  >
                    Explore Membership Plans
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
